"use client";

import type { WorkflowStepSignal } from "@repo/core/client";
import { BeforeAfter } from "@ui/components/before-after";
import { DeltaBadge } from "@ui/components/delta-badge";
import { Sparkline } from "@ui/components/sparkline";
import { CHART_SERIES } from "@ui/lib/chart-palette";
import { CheckCircle2Icon, CircleIcon } from "lucide-react";

/**
 * A workflow step's completion signal: the small piece of evidence rendered
 * beside the step's criterion. Each variant reads the property state the
 * workflow engine observes (report count, prompt coverage, active projects,
 * …); the design-lab prototypes and the blog callouts feed it fixture data.
 * Graduated from the design lab (which re-exports this component).
 */
export function StepSignal({ signal }: { signal: WorkflowStepSignal }) {
	switch (signal.kind) {
		case "count":
			return (
				<div>
					<p className="text-xs font-semibold text-muted-foreground">
						{signal.label}
					</p>
					<p className="mt-1 font-mono text-2xl font-extrabold tabular-nums">
						{signal.value}
						{signal.target != null && (
							<span className="text-base font-semibold text-muted-foreground">
								{" "}
								/ {signal.target}
							</span>
						)}
					</p>
					{signal.caption && (
						<p className="mt-1 text-xs text-muted-foreground">
							{signal.caption}
						</p>
					)}
				</div>
			);
		case "score": {
			const pct = Math.min(100, Math.max(0, signal.value));
			const target = Math.min(100, Math.max(0, signal.target));
			return (
				<div>
					<div className="flex items-baseline justify-between gap-2">
						<p className="text-xs font-semibold text-muted-foreground">
							{signal.label}
						</p>
						<p className="font-mono text-2xl font-extrabold tabular-nums">
							{signal.value}
						</p>
					</div>
					<div className="relative mt-2 h-2 rounded-full bg-muted">
						<div
							className="h-2 rounded-full bg-primary"
							style={{ width: `${pct}%` }}
						/>
						<div
							className="absolute top-[-3px] h-[14px] w-px bg-foreground/40"
							style={{ left: `${target}%` }}
							aria-hidden
						/>
					</div>
					<p className="mt-1 text-xs text-muted-foreground">
						target {signal.target}
					</p>
				</div>
			);
		}
		case "trend":
			return (
				<div>
					<div className="flex items-center justify-between gap-2">
						<p className="text-xs font-semibold text-muted-foreground">
							{signal.label}
						</p>
						<DeltaBadge
							change={signal.change}
							suffix={signal.suffix}
							lowerIsBetter={signal.lowerIsBetter}
						/>
					</div>
					<div className="mt-2">
						<Sparkline
							data={signal.series}
							color={CHART_SERIES[0]}
							height={56}
						/>
					</div>
				</div>
			);
		case "beforeAfter": {
			const { kind: _, ...props } = signal;
			return <BeforeAfter {...props} />;
		}
		case "checklist":
			return (
				<ul className="space-y-1.5">
					{signal.items.map((item) => (
						<li
							key={item.label}
							className="flex items-center gap-2 text-sm"
						>
							{item.done ? (
								<CheckCircle2Icon
									className="size-4 shrink-0 text-primary"
									aria-hidden
								/>
							) : (
								<CircleIcon
									className="size-4 shrink-0 text-muted-foreground"
									aria-hidden
								/>
							)}
							<span
								className={
									item.done
										? "text-foreground"
										: "text-muted-foreground"
								}
							>
								{item.label}
								{item.done ? "" : " · pending"}
							</span>
						</li>
					))}
				</ul>
			);
		default:
			return null;
	}
}
